import { useQuery } from '@tanstack/react-query'
import { useVisionClient } from '@/app/vision-client-provider'
import {
  canManageMsl,
  canManageMslFromRoles,
  canWriteOms,
  canWriteOmsFromRoles,
  isAdminTier,
  rolesFromAccessToken,
} from '@/lib/access-roles'
import { env, HAS_DEV_JWT, OMS_WRITES_ENABLED, PO_CANCELLATION_ENABLED } from '@/lib/env'
import type { Me } from '@/lib/vision/schemas'

/**
 * Current operator as Vision sees them from Shield-derived identity.
 * Shield owns role management; Pulse only consumes the effective role.
 */
export function usePulseMe() {
  const client = useVisionClient()
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => client.getMe(),
    staleTime: 60_000,
    retry: false,
  })
}

/**
 * ACK / warehouse-release. Prefers {@code GET /v1/me} role.
 * Fail-closed until /me resolves — do not fall back to Entra JWT, which
 * can still say Manager after Shield demotes the operator to Viewer.
 * Local {@code VITE_DEV_JWT_OVERRIDE} is the exception: local Vision often
 * has no Entra decoder, so /me 401s; the override token's roles keep Upload
 * / ACK visible on a laptop.
 */
export function useCanWriteOms(): boolean {
  const me = usePulseMe()
  if (me.data) return canWriteOms(me.data.role)
  if (HAS_DEV_JWT && me.isError) {
    return canWriteOmsFromRoles(rolesFromAccessToken(env.VITE_DEV_JWT_OVERRIDE))
  }
  return false
}

/**
 * MSL download / upload — Config Manager+. Same /me preference as
 * {@link useCanWriteOms}; DEV JWT fallback only when /me errors locally.
 */
export function useCanManageMsl(): boolean {
  const me = usePulseMe()
  if (me.data) return canManageMsl(me.data.role)
  if (HAS_DEV_JWT && me.isError) {
    return canManageMslFromRoles(rolesFromAccessToken(env.VITE_DEV_JWT_OVERRIDE))
  }
  return false
}

/**
 * Admin-only repair actions, fail-closed from Shield-derived /me. No Entra /
 * {@code HAS_DEV_JWT} fallback: destructive repairs should not outlive a Shield
 * role change. A local bundle without {@link OMS_WRITES_ENABLED} stays read-only.
 */
export function useCanResyncAmazonStatus(): boolean {
  return useHasShieldRole(isAdminTier)
}

/** Admin-only channel-wide MSD and Vinculum sync controls. */
export function useCanAdministerSyncs(): boolean {
  return useHasShieldRole(isAdminTier)
}

function useHasShieldRole(allows: (role: Me['role']) => boolean): boolean {
  const me = usePulseMe()
  if (!OMS_WRITES_ENABLED) return false
  if (me.isLoading || me.isError || !me.data) return false
  return allows(me.data.role)
}

export function useCanCancelSalesOrder(): boolean {
  return useHasShieldRole(isAdminTier)
}

/** Operator+ canonical PO cancellation, gated independently during rollout. */
export function useCanCancelPurchaseOrder(): boolean {
  const canWrite = useHasShieldRole(canWriteOms)
  return PO_CANCELLATION_ENABLED && canWrite
}

/** Pull Vinculum warehouse status for one wave. Operator+ Shield role, fail-closed. */
export function useCanRefreshWarehouseStatus(): boolean {
  return useHasShieldRole(canWriteOms)
}
